import { useCallback, useMemo, useState } from 'react';
import { View, Pressable, Linking, Platform, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Body, Caption, SubHeading, ThemedText } from '@/components/ui/Text';
import type { Trip } from '@/services/trips';
import type { Booking } from '@/services/bookings';
import type { ItineraryItem } from '@/services/itinerary';
import { copyToClipboard } from '@/utils/clipboard';

const HOTEL_CHECKIN_PATTERN = /\bcheck[\s-]*in\b/i;
const HOTEL_CHECKOUT_PATTERN = /\bcheck[\s-]*out\b|\bbag\s*transfer\b/i;
const NON_LODGING_HOTEL_TITLE_PATTERN = /\b(rest|reset|free\s*time|downtime|break|nap|siesta)\b/i;

type WhereImStayingCardProps = {
  trip: Trip;
  bookings: Booking[];
  itineraryItems: ItineraryItem[];
};

export function WhereImStayingCard({ trip, bookings, itineraryItems }: WhereImStayingCardProps) {
  const theme = useTheme();
  const stays = useMemo(() => {
    const bookingStays = bookings
      .filter((booking) => booking.type === 'hotel')
      .map((booking) => ({
        key: `booking:${booking.id}`,
        city:
          booking.location?.split(',')[0]?.trim() ||
          booking.address?.split(',')[0]?.trim() ||
          'Stay',
        title: booking.provider || booking.location || 'Accommodation',
        address: booking.address?.trim() || booking.location?.trim() || '',
        startDate: booking.checkInDate ?? booking.departureDate ?? null,
        endDate: booking.checkOutDate ?? booking.arrivalDate ?? null,
        cityIndex: null as number | null,
      }));

    const itineraryStays = itineraryItems
      .filter((item) => {
        if (item.category !== 'hotel') {
          return false;
        }

        const hasBookingBackedStay =
          Boolean(item.bookingId) || item.bookingSnapshot?.type?.toLowerCase() === 'hotel';
        const title = item.title?.trim() ?? '';
        const hasCheckInSignal = HOTEL_CHECKIN_PATTERN.test(title);
        const hasCheckOutSignal = HOTEL_CHECKOUT_PATTERN.test(title);
        const looksLikeNonLodgingTitle = NON_LODGING_HOTEL_TITLE_PATTERN.test(title);

        // Keep booking-backed stays, but suppress itinerary utility blocks like
        // "Hotel Reset", "Free Time / Hotel Reset", and "Hotel Checkout & Bag Transfer".
        if (!hasBookingBackedStay && hasCheckOutSignal && !hasCheckInSignal) {
          return false;
        }
        if (!hasBookingBackedStay && looksLikeNonLodgingTitle && !hasCheckInSignal) {
          return false;
        }

        return true;
      })
      .map((item) => ({
        key: `itinerary:${item.id}`,
        city:
          (typeof item.cityIndex === 'number' && trip.destinations?.[item.cityIndex]?.city) ||
          item.location?.split(',')[0]?.trim() ||
          item.address?.split(',')[0]?.trim() ||
          'Stay',
        title: item.title || item.location || 'Accommodation',
        address: item.address?.trim() || item.location?.trim() || '',
        startDate: item.startDate ?? null,
        endDate: item.endDate ?? null,
        cityIndex: item.cityIndex ?? null,
      }));

    const combined = [...bookingStays, ...itineraryStays];
    const seen = new Set<string>();

    return combined
      .filter((stay) => {
        const identity = [
          stay.city.toLowerCase(),
          stay.title.toLowerCase(),
          stay.address.toLowerCase(),
          stay.startDate ? new Date(stay.startDate).getTime() : 0,
        ].join('|');
        if (seen.has(identity)) {
          return false;
        }
        seen.add(identity);
        return true;
      })
      .sort((a, b) => {
        const aTime = a.startDate ? new Date(a.startDate).getTime() : Number.MAX_SAFE_INTEGER;
        const bTime = b.startDate ? new Date(b.startDate).getTime() : Number.MAX_SAFE_INTEGER;
        if (aTime !== bTime) return aTime - bTime;
        if (a.cityIndex != null && b.cityIndex != null && a.cityIndex !== b.cityIndex) {
          return a.cityIndex - b.cityIndex;
        }
        return a.city.localeCompare(b.city);
      });
  }, [bookings, itineraryItems, trip.destinations]);

  const [lodgingExpanded, setLodgingExpanded] = useState(false);
  const multi = stays.length > 1;
  const displayStays = useMemo(() => {
    if (!multi) return stays;
    return lodgingExpanded ? stays : [stays[0]];
  }, [stays, multi, lodgingExpanded]);

  const formatDateRange = useCallback((start?: Date | null, end?: Date | null) => {
    if (!start) return null;
    const startLabel = new Date(start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!end) return startLabel;
    const endLabel = new Date(end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${startLabel} - ${endLabel}`;
  }, []);

  const handleCopyAddress = useCallback(async (address: string) => {
    if (!address) return;
    try {
      await copyToClipboard(address);
      Alert.alert('Copied', 'Address copied to clipboard.');
    } catch {
      Alert.alert('Copy failed', address);
    }
  }, []);

  const handleOpenMap = useCallback(async (address: string) => {
    if (!address) return;
    const encoded = encodeURIComponent(address);
    const url =
      Platform.OS === 'ios'
        ? `http://maps.apple.com/?q=${encoded}`
        : `https://www.google.com/maps/search/?api=1&query=${encoded}`;
    try {
      await Linking.openURL(url);
    } catch {
      Alert.alert('Unable to open map', address);
    }
  }, []);

  if (stays.length === 0) {
    return null;
  }

  const actionPillStyle = (pressed: boolean) => ({
    flex: 1,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    gap: theme.spacing(0.5),
    paddingVertical: theme.spacing(1),
    paddingHorizontal: theme.spacing(1),
    borderRadius: theme.radii.pill,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.surfaceElevated,
    opacity: pressed ? 0.85 : 1,
  });

  return (
    <Card variant="outlined" style={{ marginBottom: theme.spacing(1.5) }}>
      <View style={{ padding: theme.spacing(1), gap: theme.spacing(0.75) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
          <Ionicons name="bed-outline" size={18} color={theme.colors.primary} />
          <SubHeading style={{ fontSize: 13, fontWeight: '600' }}>Where I&apos;m staying</SubHeading>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          {displayStays.map((stay, index) => {
            const previousCity = index > 0 ? displayStays[index - 1]?.city : null;
            const showCityLabel = stay.city && stay.city !== previousCity;
            const dateRange = formatDateRange(stay.startDate as Date | undefined, stay.endDate as Date | undefined);
            const titleTrim = stay.title?.trim() || '';
            const copyableLocation =
              stay.address?.trim() || titleTrim || stay.city?.trim() || '';
            const showLocationBody = Boolean(copyableLocation) && copyableLocation !== titleTrim;
            // Same featured treatment as the first lodging in multi-city trips (primary border + tint).
            const rowHighlighted = index === 0;

            return (
              <View key={stay.key} style={{ gap: theme.spacing(0.5) }}>
                {showCityLabel ? (
                  <Caption
                    color={theme.colors.textTertiary}
                    style={{ fontWeight: '500', letterSpacing: 0.2, marginBottom: theme.spacing(0.25) }}
                  >
                    {stay.city}
                  </Caption>
                ) : null}
                <View
                  style={{
                    borderWidth: rowHighlighted ? 1.5 : 1,
                    borderColor: rowHighlighted ? theme.colors.primary + '55' : theme.colors.border,
                    borderRadius: theme.radii.lg,
                    padding: theme.spacing(1.5),
                    gap: theme.spacing(0.5),
                    backgroundColor: rowHighlighted ? theme.colors.primary + '10' : theme.colors.backgroundSecondary,
                  }}
                >
                  <ThemedText variant="title" style={{ fontWeight: '600', color: theme.colors.text }}>
                    {stay.title}
                  </ThemedText>
                  {showLocationBody ? (
                    <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                      {copyableLocation}
                    </Body>
                  ) : null}
                  {dateRange ? (
                    <Caption color={theme.colors.textTertiary} style={{ marginTop: theme.spacing(0.25) }}>
                      {dateRange}
                    </Caption>
                  ) : null}
                  {copyableLocation ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        gap: theme.spacing(1),
                        marginTop: theme.spacing(1),
                      }}
                    >
                      <Pressable
                        onPress={() => handleCopyAddress(copyableLocation)}
                        style={({ pressed }) => actionPillStyle(pressed)}
                      >
                        <Ionicons name="copy-outline" size={16} color={theme.colors.text} />
                        <Caption style={{ fontWeight: '600', color: theme.colors.text }}>Copy</Caption>
                      </Pressable>
                      <Pressable
                        onPress={() => handleOpenMap(copyableLocation)}
                        style={({ pressed }) => actionPillStyle(pressed)}
                      >
                        <Ionicons name="map-outline" size={16} color={theme.colors.text} />
                        <Caption style={{ fontWeight: '600', color: theme.colors.text }}>View on Map</Caption>
                      </Pressable>
                    </View>
                  ) : null}
                </View>
              </View>
            );
          })}

          {multi && !lodgingExpanded && (
            <Pressable
              onPress={() => setLodgingExpanded(true)}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing(0.5),
                paddingVertical: theme.spacing(0.75),
                paddingHorizontal: theme.spacing(1),
                borderRadius: theme.radii.md,
                backgroundColor: theme.colors.surface,
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
                opacity: pressed ? 0.75 : 1,
              })}
              accessibilityRole="button"
              accessibilityLabel={`Show all ${stays.length} lodging entries`}
            >
              <Caption style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primary }}>
                Show all lodging ({stays.length})
              </Caption>
              <Ionicons name="chevron-down" size={16} color={theme.colors.primary} />
            </Pressable>
          )}

          {multi && lodgingExpanded && (
            <Pressable
              onPress={() => setLodgingExpanded(false)}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing(0.5),
                paddingVertical: theme.spacing(0.5),
                opacity: pressed ? 0.75 : 1,
              })}
              accessibilityRole="button"
              accessibilityLabel="Show fewer lodging entries"
            >
              <Caption style={{ fontWeight: '600', color: theme.colors.textSecondary }}>Show less</Caption>
              <Ionicons name="chevron-up" size={16} color={theme.colors.textSecondary} />
            </Pressable>
          )}
        </View>
      </View>
    </Card>
  );
}
